<script setup>
/**
 * A page-level failure: the backend is unreachable, or refused the request.
 *
 * The rule from the specification is that API errors are shown as-is and never swallowed
 * into "something went wrong". So the backend's own message is the headline; the
 * technical detail sits underneath in monospace, present for whoever wants to paste it
 * into a ticket.
 */
import { computed } from 'vue'
import { ApiError } from '@/api/client.js'

const props = defineProps({
  /** @type {import('vue').PropType<Error|null>} */
  error: { type: Object, default: null },
  /** Shown above the message, e.g. "Could not load the previews". */
  title: { type: String, default: 'An operation failed' },
  retryLabel: { type: String, default: 'Try again' },
})

defineEmits(['retry'])

const message = computed(() => props.error?.message || 'Unknown error.')

const detail = computed(() => (props.error instanceof ApiError ? props.error.detail : ''))

const context = computed(() => {
  const error = props.error
  if (!(error instanceof ApiError)) return ''
  if (error.isNetwork) {
    return 'The PreviewApp service is not answering. It may be redeploying; try again in a minute.'
  }
  if (error.isUnauthorized) return 'Your Entra ID session has expired.'
  if (error.isForbidden) return 'Your account does not have the rights it needs on this preview.'
  if (error.status >= 500) return 'The fault is on the server, not in your request.'
  return ''
})

const status = computed(() =>
  props.error instanceof ApiError && props.error.status ? `HTTP ${props.error.status}` : '',
)
</script>

<template>
  <div class="error card">
    <div class="error__head">
      <span class="error__glyph" aria-hidden="true">▲</span>
      <div>
        <p class="error__title">{{ title }}</p>
        <p class="error__message">{{ message }}</p>
      </div>
    </div>

    <p v-if="context" class="error__context">{{ context }}</p>

    <p v-if="detail || status" class="error__detail mono">
      <span v-if="status">{{ status }}</span>
      <span v-if="status && detail"> — </span>
      <span v-if="detail">{{ detail }}</span>
    </p>

    <div class="error__actions">
      <button type="button" class="btn btn--primary" @click="$emit('retry')">{{ retryLabel }}</button>
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
.error {
  padding: var(--sp-7);
  border-color: var(--st-failed-bd);
}

.error__head {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-5);
}

.error__glyph {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: var(--radius-pill);
  background: var(--st-failed-bg);
  color: var(--st-failed-fg);
  font-size: var(--text-sm);
}

.error__title {
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
}

.error__message {
  margin-top: var(--sp-2);
  color: var(--c-text);
  font-size: var(--text-md);
  line-height: var(--leading-loose);
}

.error__context {
  margin-top: var(--sp-5);
  padding-left: calc(26px + var(--sp-5));
  color: var(--c-text-muted);
  font-size: var(--text-base);
}

.error__detail {
  margin-top: var(--sp-5);
  padding: var(--sp-4) var(--sp-5);
  background: var(--c-surface-2);
  border-radius: var(--radius-md);
  color: var(--c-text-subtle);
  font-size: var(--text-sm);
  overflow-wrap: anywhere;
}

.error__actions {
  display: flex;
  gap: var(--sp-4);
  margin-top: var(--sp-6);
}
</style>
